import React, { useState, useEffect, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Tabs, TabsList, TabsTrigger, TabsContent } from './ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';
import {
  ArrowLeft, Mail, Phone, Calendar, MessageSquare, RefreshCw, User, Lock, LogOut,
  Eye, EyeOff, ShieldCheck, Bot, FileText, Languages, X
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const formatDate = (timestamp) => {
  if (!timestamp) return '—';
  const date = new Date(timestamp);
  return date.toLocaleDateString('en-GB', {
    day: '2-digit', month: 'short', year: 'numeric',
    hour: '2-digit', minute: '2-digit'
  });
};

const LANG_LABEL = {
  en: 'English', gr: 'Greek', de: 'German', fr: 'French',
  he: 'Hebrew', zh: 'Chinese', ar: 'Arabic', ru: 'Russian',
};

const Admin = () => {
  const [activeTab, setActiveTab] = useState('contact');

  // Auth
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [checkingAuth, setCheckingAuth] = useState(true);
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loginLoading, setLoginLoading] = useState(false);

  // Contact submissions
  const [submissions, setSubmissions] = useState([]);
  const [loadingContact, setLoadingContact] = useState(false);
  const [errorContact, setErrorContact] = useState(null);

  // Chat leads
  const [leads, setLeads] = useState([]);
  const [sessionsMap, setSessionsMap] = useState({});
  const [loadingChat, setLoadingChat] = useState(false);
  const [errorChat, setErrorChat] = useState(null);

  // Transcript modal
  const [transcript, setTranscript] = useState(null);
  const [transcriptLoading, setTranscriptLoading] = useState(false);

  const navigate = useNavigate();

  useEffect(() => {
    const checkAuth = async () => {
      try {
        const response = await fetch(`${API_URL}/api/admin/verify`, { credentials: 'include' });
        if (response.ok) setIsAuthenticated(true);
      } catch (err) { /* not authed */ }
      finally { setCheckingAuth(false); }
    };
    checkAuth();
  }, []);

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoginLoading(true);
    try {
      const response = await fetch(`${API_URL}/api/admin/login`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ password }),
      });
      if (response.ok) {
        setIsAuthenticated(true);
        setPassword('');
        toast.success('Login successful');
      } else {
        const data = await response.json();
        toast.error(data.detail || 'Invalid password');
      }
    } catch (err) {
      toast.error('Login failed. Please try again.');
    } finally {
      setLoginLoading(false);
    }
  };

  const handleLogout = async () => {
    try {
      await fetch(`${API_URL}/api/admin/logout`, { method: 'POST', credentials: 'include' });
    } catch (err) { /* ignore */ }
    setIsAuthenticated(false);
    setSubmissions([]);
    setLeads([]);
    setSessionsMap({});
    toast.success('Logged out successfully');
  };

  const fetchSubmissions = useCallback(async () => {
    setLoadingContact(true);
    setErrorContact(null);
    try {
      const response = await fetch(`${API_URL}/api/contact`, { credentials: 'include' });
      if (!response.ok) throw new Error('Failed to fetch submissions');
      const data = await response.json();
      data.sort((a, b) => new Date(b.timestamp) - new Date(a.timestamp));
      setSubmissions(data);
    } catch (err) {
      setErrorContact(err.message);
    } finally {
      setLoadingContact(false);
    }
  }, []);

  const fetchChatLeads = useCallback(async () => {
    setLoadingChat(true);
    setErrorChat(null);
    try {
      const [leadsRes, sessionsRes] = await Promise.all([
        fetch(`${API_URL}/api/chat/admin/leads`, { credentials: 'include' }),
        fetch(`${API_URL}/api/chat/admin/sessions`, { credentials: 'include' }),
      ]);
      if (!leadsRes.ok) throw new Error('Failed to fetch chat leads');
      if (!sessionsRes.ok) throw new Error('Failed to fetch chat sessions');
      const leadsData = await leadsRes.json();
      const sessionsData = await sessionsRes.json();
      leadsData.sort((a, b) => new Date(b.captured_at) - new Date(a.captured_at));
      const sMap = {};
      sessionsData.forEach(s => { sMap[s.session_id] = s; });
      setLeads(leadsData);
      setSessionsMap(sMap);
    } catch (err) {
      setErrorChat(err.message);
    } finally {
      setLoadingChat(false);
    }
  }, []);

  useEffect(() => {
    if (!isAuthenticated) return;
    if (activeTab === 'contact') fetchSubmissions();
    if (activeTab === 'chat') fetchChatLeads();
  }, [isAuthenticated, activeTab, fetchSubmissions, fetchChatLeads]);

  const openTranscript = async (sessionId) => {
    setTranscriptLoading(true);
    setTranscript({ sessionId, messages: [], session: null });
    try {
      const res = await fetch(`${API_URL}/api/chat/admin/transcript/${sessionId}`, {
        credentials: 'include'
      });
      if (!res.ok) throw new Error('Failed to load transcript');
      const data = await res.json();
      setTranscript({
        sessionId,
        session: data.session,
        messages: data.messages || []
      });
    } catch (err) {
      toast.error('Could not load transcript');
      setTranscript(null);
    } finally {
      setTranscriptLoading(false);
    }
  };

  const closeTranscript = () => setTranscript(null);

  if (checkingAuth) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-100 to-slate-200 flex items-center justify-center">
        <RefreshCw className="w-8 h-8 text-slate-400 animate-spin" />
      </div>
    );
  }

  if (!isAuthenticated) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-100 to-slate-200 flex items-center justify-center p-4">
        <Card className="w-full max-w-md bg-white shadow-2xl border-none">
          <CardHeader className="text-center pb-2">
            <div className="w-16 h-16 bg-gradient-to-br from-[#d4845f] to-[#c17552] rounded-full flex items-center justify-center mx-auto mb-4">
              <Lock className="w-8 h-8 text-white" />
            </div>
            <CardTitle className="text-2xl text-slate-800">Admin Access</CardTitle>
            <p className="text-slate-500 text-sm mt-2">Enter password to view contact submissions</p>
          </CardHeader>
          <CardContent className="pt-4">
            <form onSubmit={handleLogin} className="space-y-4">
              <div className="relative">
                <Input
                  type={showPassword ? 'text' : 'password'}
                  placeholder="Enter admin password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="pr-10"
                  data-testid="admin-password-input"
                  autoFocus
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                >
                  {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
              <Button
                type="submit"
                className="w-full bg-[#d4845f] hover:bg-[#c17552] text-white"
                disabled={loginLoading || !password}
                data-testid="admin-login-button"
              >
                {loginLoading ? (
                  <><RefreshCw className="w-4 h-4 mr-2 animate-spin" />Verifying...</>
                ) : (
                  <><Lock className="w-4 h-4 mr-2" />Login</>
                )}
              </Button>
            </form>
            <div className="mt-6 text-center">
              <Button
                variant="ghost"
                size="sm"
                onClick={() => navigate('/')}
                className="text-slate-500 hover:text-slate-700"
              >
                <ArrowLeft className="w-4 h-4 mr-2" />
                Back to Site
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  const refreshActive = () => {
    if (activeTab === 'contact') fetchSubmissions();
    if (activeTab === 'chat') fetchChatLeads();
  };

  const loading = activeTab === 'contact' ? loadingContact : loadingChat;

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-100 to-slate-200">
      <header className="bg-white shadow-md sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => navigate('/')}
              className="flex items-center gap-2 text-slate-600 hover:text-slate-800"
              data-testid="admin-back-button"
            >
              <ArrowLeft className="w-4 h-4" />
              Back to Site
            </Button>
            <div className="h-6 w-px bg-slate-300"></div>
            <h1 className="text-xl font-semibold text-slate-800">Admin Dashboard</h1>
          </div>
          <div className="flex items-center gap-3">
            <Button
              variant="outline"
              size="sm"
              onClick={refreshActive}
              disabled={loading}
              className="flex items-center gap-2"
              data-testid="admin-refresh-button"
            >
              <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
              Refresh
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={handleLogout}
              className="flex items-center gap-2 text-red-600 hover:text-red-700 hover:border-red-300"
              data-testid="admin-logout-button"
            >
              <LogOut className="w-4 h-4" />
              Logout
            </Button>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-6 py-8">
        <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
          <TabsList className="grid w-full max-w-md grid-cols-2 mb-8" data-testid="admin-tabs">
            <TabsTrigger value="contact" data-testid="tab-contact">
              <MessageSquare className="w-4 h-4 mr-2" />
              Contact ({submissions.length})
            </TabsTrigger>
            <TabsTrigger value="chat" data-testid="tab-chat">
              <Bot className="w-4 h-4 mr-2" />
              Chat Leads ({leads.length})
            </TabsTrigger>
          </TabsList>

          {/* ============ CONTACT SUBMISSIONS ============ */}
          <TabsContent value="contact">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
              <Card className="bg-white shadow-lg border-none">
                <CardContent className="p-6">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-slate-500 mb-1">Total Inquiries</p>
                      <p className="text-3xl font-bold text-slate-800" data-testid="admin-total-count">
                        {submissions.length}
                      </p>
                    </div>
                    <div className="w-12 h-12 bg-gradient-to-br from-[#d4845f] to-[#c17552] rounded-full flex items-center justify-center">
                      <MessageSquare className="w-6 h-6 text-white" />
                    </div>
                  </div>
                </CardContent>
              </Card>

              <Card className="bg-white shadow-lg border-none">
                <CardContent className="p-6">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-slate-500 mb-1">This Week</p>
                      <p className="text-3xl font-bold text-slate-800">
                        {submissions.filter(s => {
                          const weekAgo = new Date();
                          weekAgo.setDate(weekAgo.getDate() - 7);
                          return new Date(s.timestamp) > weekAgo;
                        }).length}
                      </p>
                    </div>
                    <div className="w-12 h-12 bg-gradient-to-br from-[#a8cef1] to-[#7eb3e3] rounded-full flex items-center justify-center">
                      <Calendar className="w-6 h-6 text-white" />
                    </div>
                  </div>
                </CardContent>
              </Card>

              <Card className="bg-white shadow-lg border-none">
                <CardContent className="p-6">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-slate-500 mb-1">Today</p>
                      <p className="text-3xl font-bold text-slate-800">
                        {submissions.filter(s => {
                          const today = new Date();
                          return new Date(s.timestamp).toDateString() === today.toDateString();
                        }).length}
                      </p>
                    </div>
                    <div className="w-12 h-12 bg-gradient-to-br from-emerald-400 to-emerald-500 rounded-full flex items-center justify-center">
                      <Mail className="w-6 h-6 text-white" />
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>

            {errorContact && (
              <Card className="bg-red-50 border-red-200 mb-6">
                <CardContent className="p-6 text-center text-red-600">
                  <p>Error: {errorContact}</p>
                  <Button variant="outline" className="mt-4" onClick={fetchSubmissions}>Try Again</Button>
                </CardContent>
              </Card>
            )}

            {loadingContact && (
              <div className="flex items-center justify-center py-12">
                <RefreshCw className="w-8 h-8 text-slate-400 animate-spin" />
              </div>
            )}

            {!loadingContact && !errorContact && submissions.length === 0 && (
              <Card className="bg-white shadow-lg border-none">
                <CardContent className="p-12 text-center">
                  <MessageSquare className="w-16 h-16 text-slate-300 mx-auto mb-4" />
                  <h3 className="text-xl font-semibold text-slate-600 mb-2">No Submissions Yet</h3>
                  <p className="text-slate-500">Contact form submissions will appear here.</p>
                </CardContent>
              </Card>
            )}

            {!loadingContact && !errorContact && submissions.length > 0 && (
              <div className="space-y-4" data-testid="admin-submissions-list">
                {submissions.map((submission, index) => (
                  <Card
                    key={submission.id}
                    className="bg-white shadow-lg border-none hover:shadow-xl transition-shadow duration-300"
                    data-testid={`submission-card-${index}`}
                  >
                    <CardHeader className="pb-2">
                      <div className="flex items-start justify-between">
                        <div className="flex items-center gap-3">
                          <div className="w-10 h-10 bg-gradient-to-br from-[#d4845f] to-[#c17552] rounded-full flex items-center justify-center">
                            <User className="w-5 h-5 text-white" />
                          </div>
                          <div>
                            <CardTitle className="text-lg text-slate-800">{submission.name}</CardTitle>
                            <p className="text-sm text-slate-500">{formatDate(submission.timestamp)}</p>
                          </div>
                        </div>
                        {submission.interest && submission.interest !== 'general' && (
                          <span className="px-3 py-1 bg-[#d4845f]/10 text-[#d4845f] text-sm font-medium rounded-full">
                            {submission.interest === 'details' ? 'Wants Details' : 'Wants Viewing'}
                          </span>
                        )}
                      </div>
                    </CardHeader>
                    <CardContent className="pt-2">
                      <div className="grid md:grid-cols-2 gap-4 mb-4">
                        <div className="flex items-center gap-2 text-slate-600">
                          <Mail className="w-4 h-4 text-[#7eb3e3]" />
                          <a href={`mailto:${submission.email}`} className="hover:text-[#d4845f] transition-colors">
                            {submission.email}
                          </a>
                        </div>
                        {submission.phone && (
                          <div className="flex items-center gap-2 text-slate-600">
                            <Phone className="w-4 h-4 text-[#7eb3e3]" />
                            <a href={`tel:${submission.phone}`} className="hover:text-[#d4845f] transition-colors">
                              {submission.phone}
                            </a>
                          </div>
                        )}
                      </div>
                      <div className="bg-slate-50 rounded-lg p-4">
                        <p className="text-slate-700 whitespace-pre-wrap">{submission.message}</p>
                      </div>
                      <div className="mt-4 flex items-center gap-2 text-sm">
                        <ShieldCheck className={`w-4 h-4 ${submission.privacy_consent ? 'text-emerald-500' : 'text-slate-400'}`} />
                        <span className={submission.privacy_consent ? 'text-emerald-600' : 'text-slate-500'}>
                          {submission.privacy_consent
                            ? `Privacy Policy accepted${submission.privacy_consent_timestamp ? ` on ${formatDate(submission.privacy_consent_timestamp)}` : ''}`
                            : 'Privacy consent not recorded (legacy submission)'}
                        </span>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}
          </TabsContent>

          {/* ============ CHAT LEADS ============ */}
          <TabsContent value="chat">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
              <Card className="bg-white shadow-lg border-none">
                <CardContent className="p-6">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-slate-500 mb-1">Total Chat Leads</p>
                      <p className="text-3xl font-bold text-slate-800" data-testid="chat-leads-total">
                        {leads.length}
                      </p>
                    </div>
                    <div className="w-12 h-12 bg-gradient-to-br from-[#d4845f] to-[#c17552] rounded-full flex items-center justify-center">
                      <Bot className="w-6 h-6 text-white" />
                    </div>
                  </div>
                </CardContent>
              </Card>

              <Card className="bg-white shadow-lg border-none">
                <CardContent className="p-6">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-slate-500 mb-1">This Week</p>
                      <p className="text-3xl font-bold text-slate-800">
                        {leads.filter(l => {
                          const weekAgo = new Date();
                          weekAgo.setDate(weekAgo.getDate() - 7);
                          return new Date(l.captured_at) > weekAgo;
                        }).length}
                      </p>
                    </div>
                    <div className="w-12 h-12 bg-gradient-to-br from-[#a8cef1] to-[#7eb3e3] rounded-full flex items-center justify-center">
                      <Calendar className="w-6 h-6 text-white" />
                    </div>
                  </div>
                </CardContent>
              </Card>

              <Card className="bg-white shadow-lg border-none">
                <CardContent className="p-6">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-slate-500 mb-1">Today</p>
                      <p className="text-3xl font-bold text-slate-800">
                        {leads.filter(l => {
                          const today = new Date();
                          return new Date(l.captured_at).toDateString() === today.toDateString();
                        }).length}
                      </p>
                    </div>
                    <div className="w-12 h-12 bg-gradient-to-br from-emerald-400 to-emerald-500 rounded-full flex items-center justify-center">
                      <Mail className="w-6 h-6 text-white" />
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>

            {errorChat && (
              <Card className="bg-red-50 border-red-200 mb-6">
                <CardContent className="p-6 text-center text-red-600">
                  <p>Error: {errorChat}</p>
                  <Button variant="outline" className="mt-4" onClick={fetchChatLeads}>Try Again</Button>
                </CardContent>
              </Card>
            )}

            {loadingChat && (
              <div className="flex items-center justify-center py-12">
                <RefreshCw className="w-8 h-8 text-slate-400 animate-spin" />
              </div>
            )}

            {!loadingChat && !errorChat && leads.length === 0 && (
              <Card className="bg-white shadow-lg border-none">
                <CardContent className="p-12 text-center">
                  <Bot className="w-16 h-16 text-slate-300 mx-auto mb-4" />
                  <h3 className="text-xl font-semibold text-slate-600 mb-2">No Chat Leads Yet</h3>
                  <p className="text-slate-500">When visitors leave their details via the AI Concierge, they'll appear here.</p>
                </CardContent>
              </Card>
            )}

            {!loadingChat && !errorChat && leads.length > 0 && (
              <div className="space-y-4" data-testid="chat-leads-list">
                {leads.map((lead, index) => {
                  const session = sessionsMap[lead.session_id];
                  const langCode = session?.language || 'en';
                  return (
                    <Card
                      key={lead.id}
                      className="bg-white shadow-lg border-none hover:shadow-xl transition-shadow duration-300"
                      data-testid={`chat-lead-card-${index}`}
                    >
                      <CardHeader className="pb-2">
                        <div className="flex items-start justify-between">
                          <div className="flex items-center gap-3">
                            <div className="w-10 h-10 bg-gradient-to-br from-[#7eb3e3] to-[#5a9bd5] rounded-full flex items-center justify-center">
                              <Bot className="w-5 h-5 text-white" />
                            </div>
                            <div>
                              <CardTitle className="text-lg text-slate-800">{lead.name}</CardTitle>
                              <p className="text-sm text-slate-500">{formatDate(lead.captured_at)}</p>
                            </div>
                          </div>
                          <div className="flex items-center gap-2">
                            {lead.source === 'guide_pdf' && (
                              <span className="px-2 py-1 bg-[#d4845f]/10 text-[#d4845f] text-xs font-medium rounded-full inline-flex items-center gap-1">
                                <FileText className="w-3 h-3" />
                                Guide PDF
                              </span>
                            )}
                            <span className="px-3 py-1 bg-[#7eb3e3]/10 text-[#5a9bd5] text-xs font-medium rounded-full inline-flex items-center gap-1">
                              <Languages className="w-3 h-3" />
                              {LANG_LABEL[langCode] || langCode}
                            </span>
                          </div>
                        </div>
                      </CardHeader>
                      <CardContent className="pt-2">
                        <div className="grid md:grid-cols-2 gap-4 mb-4">
                          <div className="flex items-center gap-2 text-slate-600">
                            <Mail className="w-4 h-4 text-[#7eb3e3]" />
                            <a href={`mailto:${lead.email}`} className="hover:text-[#d4845f] transition-colors">
                              {lead.email}
                            </a>
                          </div>
                          {session && (
                            <div className="flex items-center gap-2 text-slate-500 text-sm">
                              <MessageSquare className="w-4 h-4 text-[#7eb3e3]" />
                              {session.message_count} messages exchanged
                            </div>
                          )}
                        </div>
                        {lead.note && (
                          <div className="bg-slate-50 rounded-lg p-4 mb-3">
                            <p className="text-xs text-slate-500 mb-1">Visitor note:</p>
                            <p className="text-slate-700 whitespace-pre-wrap">{lead.note}</p>
                          </div>
                        )}
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-2 text-sm">
                            <ShieldCheck className="w-4 h-4 text-emerald-500" />
                            <span className="text-emerald-600">
                              Consent given before chat started
                            </span>
                          </div>
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => openTranscript(lead.session_id)}
                            className="flex items-center gap-2"
                            data-testid={`view-transcript-${index}`}
                          >
                            <FileText className="w-4 h-4" />
                            View Transcript
                          </Button>
                        </div>
                      </CardContent>
                    </Card>
                  );
                })}
              </div>
            )}
          </TabsContent>
        </Tabs>
      </main>

      {/* Transcript dialog */}
      <Dialog open={!!transcript} onOpenChange={(open) => { if (!open) closeTranscript(); }}>
        <DialogContent className="max-w-2xl max-h-[80vh] overflow-hidden flex flex-col" data-testid="transcript-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <FileText className="w-5 h-5 text-[#d4845f]" />
              Chat Transcript
            </DialogTitle>
            {transcript?.session && (
              <p className="text-sm text-slate-500">
                {LANG_LABEL[transcript.session.language] || transcript.session.language} ·
                {' '}{transcript.session.message_count} messages ·
                {' '}Started {formatDate(transcript.session.created_at)}
              </p>
            )}
          </DialogHeader>
          <div className="flex-1 overflow-y-auto pr-2 space-y-3">
            {transcriptLoading && (
              <div className="flex items-center justify-center py-8">
                <RefreshCw className="w-6 h-6 text-slate-400 animate-spin" />
              </div>
            )}
            {!transcriptLoading && transcript?.messages?.length === 0 && (
              <p className="text-center text-slate-500 py-8">No messages found for this session.</p>
            )}
            {!transcriptLoading && transcript?.messages?.map((msg, idx) => (
              <div
                key={idx}
                className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}
              >
                <div
                  className={`max-w-[80%] rounded-2xl px-4 py-2 ${
                    msg.role === 'user'
                      ? 'bg-[#d4845f] text-white rounded-br-sm'
                      : 'bg-slate-100 text-slate-800 rounded-bl-sm'
                  }`}
                >
                  <p className="text-sm whitespace-pre-wrap">{msg.content}</p>
                  <p className={`text-xs mt-1 ${msg.role === 'user' ? 'text-white/70' : 'text-slate-400'}`}>
                    {formatDate(msg.timestamp)}
                  </p>
                </div>
              </div>
            ))}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Admin;
